import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { buildImportPlan, type ImportPlan, type ImportedSchedule } from '@siderproj/core';
import { api, type Project } from '../api';
import { useMe } from '../auth';
import { keys, useOrganizations, useUsers } from '../data';
import { Btn, Badge, DateInput, Field, Icon, Kpi, Loading, Modal, Select, useToast } from '../ui/kit';
import { fmtDate, fmtNum } from '../format';
import { ACCEPT, readScheduleFile } from '../scheduleImport';

const TYPES = ['Implantação', 'Expansão', 'Modernização', 'Adequação', 'Manutenção', 'Outro'];
const AREAS = ['Redução', 'Aciaria', 'Laminação', 'Sinterização', 'Coqueria', 'Alto-forno', 'Utilidades', 'Logística', 'Infraestrutura', 'Outra'];
const DOW = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
type Result = { id?: string; wbs: number; activities: number; dependencies: number; warnings: string[] };

/** Nome sugerido a partir do título do cronograma ("CRONOGRAMA BÁSICO TROCA ..." → "Troca ..."). */
function suggestName(title: string) {
  const t = title.replace(/^\s*cronograma(\s+b[aá]sico|\s+executivo|\s+geral)?\s*/i, '').replace(/[.\s]+$/, '').replace(/\s*-\s*/g, ' - ').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t !== t.toUpperCase()) return t;
  const low = t.toLowerCase().split(' ').map((w, i) => /\d/.test(w) ? w.toUpperCase() : w);
  return low.join(' ').replace(/^./, c => c.toUpperCase());
}

/**
 * Importa um cronograma do MS Project (PDF impresso, XML) ou planilha.
 * Sem projectId cria um projeto novo; com projectId preenche o cronograma vazio de um projeto existente.
 */
export function ImportScheduleDialog({ project, onClose }: { project?: Project; onClose: () => void }) {
  const toast = useToast(); const qc = useQueryClient(); const nav = useNavigate(); const me = useMe();
  const isNew = !project;
  const orgs = useOrganizations(isNew); const users = useUsers(isNew);
  const owner = orgs.data?.find(o => o.isOwner);
  const [file, setFile] = useState<File | null>(null); const [reading, setReading] = useState(false); const [err, setErr] = useState('');
  const [sched, setSched] = useState<ImportedSchedule | null>(null);
  const [busy, setBusy] = useState(false); const [over, setOver] = useState(false);
  const [f, setF] = useState({ code: '', name: '', type: 'Manutenção', area: '', clientOrganizationId: null as string | null, managerId: null as string | null, contractEndDate: null as string | null, workdays: [1, 2, 3, 4, 5] });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const input = useRef<HTMLInputElement>(null);
  const workdays = project?.workdays ?? f.workdays;

  const plan = useMemo<{ p: ImportPlan | null; e: string }>(() => {
    if (!sched?.tasks.length) return { p: null, e: '' };
    try { return { p: buildImportPlan(sched.tasks, { workdays }), e: '' }; } catch (e) { return { p: null, e: (e as Error).message }; }
  }, [sched, workdays.join(',')]);

  const load = async (file: File) => {
    setFile(file); setErr(''); setSched(null); setReading(true);
    try {
      const s = await readScheduleFile(file);
      if (!s.tasks.length) throw new Error(s.warnings[0] ?? 'Não encontrei tarefas neste arquivo.');
      setSched(s);
      if (isNew) {
        const p = buildImportPlan(s.tasks, { workdays: f.workdays });
        const nm = suggestName(s.title) || file.name.replace(/\.[^.]+$/, '');
        const lastFinish = s.tasks.map(t => t.finish?.slice(0, 10)).filter(Boolean).sort().at(-1) ?? p.projectFinish;
        setF(x => ({ ...x, name: x.name || nm, contractEndDate: x.contractEndDate ?? lastFinish, area: x.area || (/aciaria|conversor|convertedor/i.test(s.title + s.tasks.map(t => t.name).join(' ')) ? 'Aciaria' : '') }));
      }
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setReading(false); }
  };

  const confirm = async () => {
    if (!sched || !plan.p) return;
    const body = { tasks: sched.tasks, fileName: file?.name ?? '', source: sched.source };
    if (isNew) {
      const e: Record<string, string> = {};
      if (f.code.trim().length < 2) e.code = 'Informe o código.';
      else if (!/^[A-Z0-9][A-Z0-9._\-/]*$/i.test(f.code.trim())) e.code = 'Use letras, números, ponto, hífen ou barra.';
      if (f.name.trim().length < 3) e.name = 'Informe o nome.';
      if (f.area.trim().length < 2) e.area = 'Informe a área.';
      if (!f.contractEndDate) e.contractEndDate = 'Informe o término contratual.';
      else if (f.contractEndDate < plan.p.projectStart) e.contractEndDate = 'Término antes do início do cronograma.';
      if (!f.workdays.length) e.workdays = 'Marque ao menos um dia útil.';
      setErrs(e); if (Object.keys(e).length) return;
    }
    setBusy(true);
    try {
      if (isNew) {
        const r = await api.post<Result & { id: string }>('/api/projects/import', { ...body, project: { code: f.code.trim().toUpperCase(), name: f.name.trim(), type: f.type, area: f.area.trim(), status: 'Ativo', clientOrganizationId: f.clientOrganizationId, managerId: f.managerId, startDate: plan.p.projectStart, contractEndDate: f.contractEndDate, workdays: f.workdays, progressBasis: 'atividades', participantOrganizationIds: f.clientOrganizationId ? [f.clientOrganizationId] : [] } });
        toast.ok(`Projeto criado com ${r.activities} atividades e ${r.wbs} itens de EAP.`);
        await qc.invalidateQueries({ queryKey: keys.projects }); onClose(); nav(`/p/${r.id}/cronograma?aba=gantt`);
      } else {
        const r = await api.post<Result>(`/api/projects/${project.id}/schedule/import`, body);
        toast.ok(`Cronograma importado: ${r.activities} atividades e ${r.wbs} itens de EAP.`);
        await qc.invalidateQueries({ queryKey: ['project', project.id] }); onClose();
      }
    } catch (x) { toast.err(x); } finally { setBusy(false); }
  };

  const p = plan.p;
  const kind = useMemo(() => {
    const m = new Map<number, { as: 'eap' | 'atividade'; code: string; collapsed?: boolean }>();
    if (p) { for (const w of p.wbs) m.set(w.sourceId, { as: 'eap', code: w.code }); for (const a of p.activities) m.set(a.sourceId, { as: 'atividade', code: a.code, collapsed: a.collapsed }); }
    return m;
  }, [p]);

  return (
    <Modal title={isNew ? 'Importar projeto' : 'Importar cronograma'} size="wide" onClose={onClose}
      foot={<><span className="small muted">{sched ? `${file?.name} · ${sched.tasks.length} linhas lidas` : 'Nada é gravado até você confirmar.'}</span><span className="spacer" />
        <button className="btn" onClick={onClose}>Cancelar</button>
        <Btn className="btn primary" why={busy ? 'Aguarde: importando.' : !p && 'Escolha o arquivo do cronograma.'} onClick={confirm}>{busy ? 'Importando...' : isNew ? 'Criar projeto' : 'Importar'}</Btn></>}>
      <div className="stack">
        {!sched && (
          <div className={'dropzone' + (over ? ' over' : '')} role="button" tabIndex={0}
            onClick={() => input.current?.click()} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={e => { e.preventDefault(); setOver(false); const x = e.dataTransfer.files?.[0]; if (x) void load(x); }}>
            <Icon name="upload" />
            <strong>{reading ? `Lendo ${file?.name}...` : 'Arraste o arquivo aqui ou clique para escolher'}</strong>
            <span className="small muted">Cronograma do MS Project impresso em PDF, XML do MS Project (Arquivo › Salvar como › XML) ou planilha .xlsx/.csv com Id, Nome, Nível, Duração, Início, Término e Predecessoras.</span>
            <input ref={input} type="file" hidden accept={ACCEPT} aria-label="Arquivo do cronograma" onChange={e => { const x = e.target.files?.[0]; if (x) void load(x); e.target.value = ''; }} />
          </div>
        )}
        {reading && sched && <Loading />}
        {err && <div className="callout crit" role="alert">{err}</div>}
        {plan.e && <div className="callout crit" role="alert">{plan.e}</div>}
        {sched && p && (<>
          <div className="row">
            <div style={{ minWidth: 0 }}><div className="eyebrow">Arquivo lido</div><strong>{sched.title || file?.name}</strong></div>
            <span className="spacer" />
            <button className="btn ghost" onClick={() => { setSched(null); setFile(null); setErr(''); }}>Trocar arquivo</button>
          </div>
          <div className="kpis">
            <Kpi label="Itens de EAP" value={p.wbs.length} sub="tarefas-resumo" />
            <Kpi label="Atividades" value={p.activities.length} sub={`${p.activities.filter(a => a.milestone).length} marco(s)`} />
            <Kpi label="Dependências" value={p.dependencyCount} />
            <Kpi label="Período" value={<span style={{ fontSize: 17 }}>{fmtDate(p.projectStart)} a {fmtDate(p.projectFinish)}</span>} />
          </div>
          {[...sched.warnings, ...p.warnings].map((w, i) => <div key={i} className="callout warn small">{w}</div>)}
          {!isNew && <div className="callout info small">As tarefas-resumo viram itens da EAP e as demais viram atividades, com as predecessoras do arquivo. O projeto precisa estar com o cronograma vazio.</div>}

          {isNew && (
            <div className="form-grid">
              <div className="form-section">Dados do projeto</div>
              <Field label="Código" required error={errs.code} help="Ex.: TER-K2-01"><input className="input mono" value={f.code} onChange={e => setF({ ...f, code: e.target.value.toUpperCase() })} /></Field>
              <Field label="Nome" required error={errs.name}><input className="input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></Field>
              <Field label="Tipo" required><Select value={f.type} onChange={x => setF({ ...f, type: x ?? 'Outro' })} options={TYPES.map(t => ({ value: t, label: t }))} /></Field>
              <Field label="Área da usina" required error={errs.area}><input className="input" list="sp-areas-imp" value={f.area} onChange={e => setF({ ...f, area: e.target.value })} /><datalist id="sp-areas-imp">{AREAS.map(a => <option key={a} value={a} />)}</datalist></Field>
              <Field label="Cliente"><Select value={f.clientOrganizationId} onChange={x => setF({ ...f, clientOrganizationId: x })} empty="Nenhum" options={(orgs.data ?? []).filter(o => !o.isOwner).map(o => ({ value: o.id, label: `${o.name} · ${o.type}` }))} /></Field>
              <Field label="Gerente do projeto" help="Se vazio, você será o gerente."><Select value={f.managerId} onChange={x => setF({ ...f, managerId: x })} empty={`${me.name} (eu)`} options={(users.data ?? []).filter(u => u.active && u.organizationId === owner?.id && u.id !== me.id).map(u => ({ value: u.id, label: u.name }))} /></Field>
              <Field label="Início" help="Primeira tarefa do arquivo."><input className="input" value={fmtDate(p.projectStart)} disabled /></Field>
              <Field label="Término contratual" required error={errs.contractEndDate}><DateInput value={f.contractEndDate} onChange={x => setF({ ...f, contractEndDate: x })} /></Field>
              <Field label="Dias úteis do calendário" error={errs.workdays} full group help="As durações do arquivo são convertidas para dias úteis deste calendário.">
                <div className="row">{DOW.map((d, i) => <label key={d} className="check"><input type="checkbox" checked={f.workdays.includes(i)} onChange={e => setF({ ...f, workdays: e.target.checked ? [...f.workdays, i].sort() : f.workdays.filter(x => x !== i) })} />{d}</label>)}</div>
              </Field>
            </div>
          )}

          <div className="panel" style={{ maxHeight: 380, overflow: 'auto' }}>
            <table className="t">
              <thead><tr><th className="num">Id</th><th>Tarefa</th><th className="num">Duração</th><th>Início</th><th>Término</th><th>Predecessoras</th><th>Entra como</th></tr></thead>
              <tbody>
                {sched.tasks.map(t => {
                  const k = kind.get(t.id);
                  return (
                    <tr key={t.id}>
                      <td className="num mono">{t.id}</td>
                      <td className="wrap" style={{ paddingLeft: 10 + (t.level - 1) * 14, fontWeight: k?.as === 'eap' ? 600 : undefined }}>{t.name}</td>
                      <td className="num nowrap">{t.milestone ? 'marco' : t.hours === null ? '' : `${fmtNum(t.hours, t.hours % 1 ? 1 : 0)} h`}</td>
                      <td className="nowrap">{t.start ? fmtDate(t.start.slice(0, 10)) : ''}</td>
                      <td className="nowrap">{t.finish ? fmtDate(t.finish.slice(0, 10)) : ''}</td>
                      <td className="mono small">{t.preds.map(x => `${x.id}${x.type !== 'FS' ? x.type : ''}${x.lagHours ? (x.lagHours > 0 ? '+' : '') + fmtNum(x.lagHours / 8, 1) + 'd' : ''}`).join('; ')}</td>
                      <td className="nowrap">{k ? (k.as === 'eap' ? <Badge>EAP {k.code}</Badge> : <><Badge tone="info">Atividade {k.code}</Badge>{k.collapsed && <> <Badge tone="warn">recolhida</Badge></>}</>) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>)}
      </div>
    </Modal>
  );
}
